import { useMemo } from "react";
import { faCaretLeft, faCaretRight, faStepBackward, faStepForward, faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { hasValue, styles } from "@repo/helpers";

import { DropDownList } from "../../dropDownList";
import style from "./style.module.scss";
import { MarkupProps } from "./types";

export default function PaginationMarkup(props: MarkupProps) {
	const { currentPage, pages, pagesToDisplay, setPage, totalPages, totalItems, itemsPerPage, canSetItemsPerPage } =
		props;
	const startItem = useMemo(() => (currentPage - 1) * itemsPerPage + 1, [currentPage, itemsPerPage]);
	const endItem = useMemo(() => {
		const item = currentPage * itemsPerPage;
		if (item > totalItems) return totalItems;
		return item;
	}, [currentPage, totalItems, itemsPerPage]);
	const halfWay = useMemo(() => Math.ceil(itemsPerPage / 2), [itemsPerPage]);

	const pageSizeSet = useMemo(() => {
		const set = new Set(props.pageSizes);
		set.add(itemsPerPage);
		return Array.from(set);
	}, [props.pageSizes, itemsPerPage]);
	const pageSizes = useMemo(
		() =>
			pageSizeSet
				.sort((a, b) => a - b)
				.map((num) => {
					return { value: num, label: `${num} items per page` };
				}),
		[pageSizeSet],
	);
	return <div className={style.pagination}>{getPaginationDisplay()}</div>;

	function getPaginationDisplay() {
		if (!hasValue(currentPage) || !hasValue(totalItems) || !hasValue(itemsPerPage)) return null;

		const morePagesPrev = totalPages > pagesToDisplay && currentPage > halfWay + 1 && (
			<div className={getClassName()} onClick={() => setPage(currentPage - itemsPerPage)} title={"More pages"}>
				...
			</div>
		);
		const morePagesNext = totalPages > pagesToDisplay && totalPages - currentPage > halfWay && (
			<div className={getClassName()} onClick={() => setPage(currentPage + itemsPerPage)} title={"More pages"}>
				...
			</div>
		);
		return (
			<>
				<div className={style.paginationGroup}>
					<div onClick={() => setPage(1)} className={getClassName(currentPage === 1)}>
						<FontAwesomeIcon icon={faStepBackward} />
					</div>
					<div onClick={() => setPage(currentPage - 1)} className={getClassName(currentPage === 1)}>
						<FontAwesomeIcon icon={faCaretLeft} />
					</div>
					{morePagesPrev}
					{pages.map((page) => (
						<div
							onClick={() => setPage(page)}
							className={getClassName(currentPage === page, currentPage === page)}
							key={page}
						>
							{page}
						</div>
					))}
					{morePagesNext}
					<div onClick={() => setPage(currentPage + 1)} className={getClassName(currentPage === totalPages)}>
						<FontAwesomeIcon icon={faCaretRight} />
					</div>
					<div onClick={() => setPage(totalPages)} className={getClassName(currentPage === totalPages)}>
						<FontAwesomeIcon icon={faStepForward} />
					</div>
					<div className={style.totalCount}>
						{totalItems === 0 ? "No items to display" : `${startItem}-${endItem} of ${totalItems}`}
					</div>
				</div>
				<div className={style.paginationGroup}>
					{totalItems > 0 && canSetItemsPerPage && (
						<div className={style.totalCount} style={{ display: "inline-flex", alignItems: "center" }}>
							<DropDownList
								id="pageSizeSelector"
								values={pageSizes}
								value={itemsPerPage}
								inline
								disabled={totalItems < 5}
								changeHandler={(v) => props.setItemsPerPage(v ?? props.itemsPerPage)}
								label=""
							/>
						</div>
					)}
					<div className={style.refresh} onClick={props.refresh}>
						<FontAwesomeIcon icon={faSync} />
					</div>
				</div>
			</>
		);
	}
}

function getClassName(disabled = false, active = false) {
	return styles(style.paginationPage, disabled && style.disabled, active && style.active);
}
